import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import ContactForm from '../components/ContactForm';
import { getOptimizedImageUrl } from '../lib/cms';

export default function Forside() {
  const targetGroups = [
    'ADHD',
    'Personlighedsforstyrrelser',
    'Autisme',
    'Misbrug',
    'Omsorgssvigt',
    'Socialt udsatte',
    'Udadreagerende adfærd',
    'Selvskadende adfærd',
    'Krænkende adfærd',
    'Uhensigtsmæssig adfærd',
  ];

  const services = [
    {
      title: 'Bostøtte',
      desc: 'Individuel og fleksibel bostøtte til borgere i eget hjem eller bofællesskab i hele Jylland og på Fyn, med fokus på struktur, tryghed og udvikling i hverdagen.',
      path: '/bostotte',
      btnText: 'Læs mere om bostøtte',
      icon: 'fa-house-user',
      img: 'https://assets.auxodev.dk/assets/001da5eb-368a-49cd-a912-65694d60b551/akut-vikaren-hverdagsstoette-i-hjemmet.webp',
      alt: 'Bostøtte og hverdagsstøtte i borgerens eget hjem',
      position: 'center 20%',
    },
    {
      title: 'Aflastning – hjemme og ude',
      desc: 'Fleksibel aflastning i hele Jylland og på Fyn, der giver familier og bosteder et nødvendigt afbræk – både i hjemmet og på aflastningsophold uden for hjemmet.',
      path: '/aflastning',
      btnText: 'Læs mere om aflastning',
      icon: 'fa-hand-holding-heart',
      img: 'https://assets.auxodev.dk/assets/001da5eb-368a-49cd-a912-65694d60b551/akut-vikaren-borger-og-medarbejder-udendoers.webp',
      alt: 'Aflastning og ledsagelse udendørs med medarbejder og borger',
      position: 'center 34%',
    },
    {
      title: 'Akut anbringelse',
      desc: 'Når der skal handles hurtigt. Vi tilbyder akut anbringelse (jf. Barnets lov § 12, stk. 3, for borgere under 18 år, og Servicelovens § 4, stk. 4, for borgere over 18 år) med kort responstid, døgnet rundt – og modtager borgere fra hele landet.',
      path: '/akut-anbringelse',
      btnText: 'Læs mere om akut anbringelse',
      icon: 'fa-shield-halved',
      img: 'https://assets.auxodev.dk/assets/001da5eb-368a-49cd-a912-65694d60b551/akut-vikaren-naervaer-og-samtale.webp',
      alt: 'Nærvær, samtale og tryghedsskabende kontakt ved akut indsats',
      position: 'center 20%',
    },
  ];

  const whyChooseUs = [
    {
      icon: 'fa-clock',
      title: 'Tilgængelig døgnet rundt',
      desc: 'Hurtig responstid, også ved akutte opgaver.',
    },
    {
      icon: 'fa-user-nurse',
      title: 'Erfarne medarbejdere',
      desc: 'Specialkompetencer inden for det social- og psykiatriske område.',
    },
    {
      icon: 'fa-book-open-reader',
      title: 'Løbende undervisning',
      desc: 'I bl.a. konflikthåndtering, low arousal og skånsom magtanvendelse.',
    },
    {
      icon: 'fa-map-location-dot',
      title: 'Dækning i hele Jylland og på Fyn',
      desc: 'Ved akut anbringelse modtager vi borgere fra hele landet.',
    },
    {
      icon: 'fa-handshake',
      title: 'Fast kontakt og kontinuitet',
      desc: 'Gennem hele forløbet med fokus på den trygge relation.',
    },
  ];

  return (
    <>
      <SEO
        title="Akut Vikaren ApS - Social- og socialpsykiatrisk vikarbureau"
        description="Akutvikaren – tryghed og genkendelighed, når det gælder. Bostøtte, aflastning og akut anbringelse i Jylland, på Fyn og hele landet."
        ogImage="https://assets.auxodev.dk/assets/001da5eb-368a-49cd-a912-65694d60b551/akut-vikaren-samtale-og-omsorg.webp"
      />

      {/* 1. HERO SECTION */}
      <section className="relative bg-[#041c2c] text-white pt-12 pb-16 lg:pt-28 lg:pb-32 overflow-hidden border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-stretch">
            {/* Left Content */}
            <div className="lg:col-span-7 space-y-6 flex flex-col justify-between">
              <div className="space-y-6">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-[1.18]">
                  Akutvikaren – tryghed og genkendelighed, når det gælder
                </h1>

                <p className="text-slate-300 text-base sm:text-lg leading-relaxed max-w-2xl font-normal">
                  Vi leverer erfarne vikarer og akutte løsninger til det social- og psykiatriske område. Bostøtte, aflastning og vikardækning tilbyder vi i hele Jylland og på Fyn, mens vi ved akut anbringelse modtager borgere fra hele landet.
                </p>

                <div className="p-4 rounded-xl bg-slate-900/80 border-l-4 border-[#b39366] text-slate-200 text-sm font-medium">
                  <p className="flex items-center gap-2.5">
                    <i className="fa-solid fa-circle-check text-[#b39366] text-base shrink-0" aria-hidden="true"></i>
                    <span>Fagligt stærke og genkendelige medarbejdere – også når det brænder på.</span>
                  </p>
                </div>

                {/* CTAs */}
                <div className="flex flex-wrap items-center gap-3 pt-2">
                  <Link
                    to="/kontakt"
                    className="inline-flex items-center justify-center gap-2 bg-[#b39366] hover:bg-[#a18155] text-white font-semibold text-base px-6 py-3.5 rounded shadow-sm hover:shadow transition-all"
                  >
                    <span>Kontakt os</span>
                    <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                  </Link>

                  <Link
                    to="/bliv-vikar"
                    className="inline-flex items-center justify-center gap-2 bg-slate-800/90 hover:bg-slate-700 text-white font-semibold text-base px-5 py-3.5 rounded border border-slate-700 hover:border-[#b39366] transition-all"
                  >
                    <i className="fa-solid fa-user-plus text-xs text-[#b39366]" aria-hidden="true"></i>
                    <span>Bliv vikar</span>
                  </Link>

                  <a
                    href="tel:+4523964042"
                    className="inline-flex items-center justify-center gap-2 bg-slate-900/60 hover:bg-slate-800 text-slate-200 hover:text-white font-medium text-base px-4 py-3.5 rounded border border-slate-800 transition-all whitespace-nowrap"
                  >
                    <i className="fa-solid fa-phone text-xs text-[#b39366]" aria-hidden="true"></i>
                    <span className="whitespace-nowrap">(+45) 23 96 40 42</span>
                  </a>
                </div>
              </div>

              {/* Badges / Value points */}
              <div className="pt-6 grid grid-cols-2 sm:grid-cols-3 gap-4 border-t border-slate-800/80 text-xs text-slate-300">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#b39366]" aria-hidden="true"></i>
                  <span>Døgnåben 24/7/365</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#b39366]" aria-hidden="true"></i>
                  <span>Hurtig responstid</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#b39366]" aria-hidden="true"></i>
                  <span>Fokus på relationen</span>
                </div>
              </div>
            </div>

            {/* Right Hero Visual */}
            <div className="lg:col-span-5 relative flex flex-col h-full min-h-[380px] lg:min-h-0">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-slate-800/60 w-full h-full flex-1 min-h-[340px] lg:min-h-full">
                <img
                  src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/001da5eb-368a-49cd-a912-65694d60b551/akut-vikaren-samtale-og-omsorg.webp', 800, 85)}
                  alt="Nærværende samtale og omsorg mellem medarbejdere og borger hos Akutvikaren"
                  className="w-full h-full object-cover object-top absolute inset-0"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#041c2c]/75 via-transparent to-transparent pointer-events-none"></div>
                <div className="absolute bottom-4 left-4 right-4 bg-slate-900/90 backdrop-blur-sm p-3.5 rounded-lg border border-slate-700/80 text-xs text-slate-200">
                  <p className="font-semibold text-white">Genkendelighed & Tryghed</p>
                  <p className="text-slate-400 mt-0.5">Vi matcher altid den rette medarbejder til opgaven.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. OM OS SECTION */}
      <section className="py-16 sm:py-20 lg:py-24 bg-[#f8f7f4] border-b border-[#eae6dd]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Portrait & Info Box */}
            <div className="lg:col-span-5 space-y-6">
              <div className="relative rounded-2xl overflow-hidden shadow-md border border-[#eae6dd] bg-white aspect-4/5">
                <img
                  src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/001da5eb-368a-49cd-a912-65694d60b551/akut-vikaren-medarbejder-portraet.webp', 600, 85)}
                  alt="Camilla Bladt - Ergoterapeut og stifter af Akutvikaren"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#041c2c] via-[#041c2c]/85 to-transparent p-6 text-white">
                  <h3 className="text-xl font-bold">Camilla Bladt</h3>
                  <p className="text-xs text-[#b39366] font-semibold uppercase tracking-wider mt-0.5">
                    Ergoterapeut med 20 års erfaring
                  </p>
                  <p className="text-xs text-slate-300 mt-1">Stifter og leder af Akutvikaren siden 2021</p>
                </div>
              </div>
            </div>

            {/* Right Text Content */}
            <div className="lg:col-span-7 space-y-6">
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-[#b39366]">Om os</span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#041c2c] tracking-tight mt-1">
                  Akutvikaren – relationsbåret faglighed og tryghed
                </h2>
              </div>

              <div className="space-y-4 text-slate-700 text-base leading-relaxed">
                <p>
                  Akutvikaren er et social- og socialpsykiatrisk vikarbureau med base i Højbjerg ved Aarhus. Vi hjælper kommuner, bosteder, opholdssteder og familier i hele Jylland og på Fyn med bostøtte, aflastning, vikardækning og meget mere. Når det gælder akut anbringelse, modtager vi borgere fra hele landet.
                </p>
                <p>
                  Bag Akutvikaren står Camilla Bladt, ergoterapeut med 20 års erfaring inden for det socialfaglige og psykiatriske område. Siden 2021 har Camilla drevet Akutvikaren med et klart fokus: at skabe genkendelighed og tryghed for både borgere og samarbejdspartnere. Vi tror på, at relationen betyder alt i arbejdet med mennesker, og vi lægger derfor stor vægt på at matche den rette medarbejder til hver opgave.
                </p>
                <p>
                  Vores medarbejdere bliver løbende undervist og opkvalificeret inden for bl.a. konflikthåndtering, skånsom magtanvendelse, lov om voksenansvar, medicinhåndtering, dokumentation og low arousal – så vi altid kan levere en fagligt kvalificeret indsats, uanset hvor kompleks opgaven er.
                </p>
                <p className="font-medium text-[#041c2c]">
                  Vi er tilgængelige døgnet rundt og svarer altid hurtigt, uanset om du kontakter os pr. telefon, sms eller mail.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. VORES MÅLGRUPPE - NICE LIST (Target Element updated) */}
      <section className="py-16 sm:py-20 bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-3 mb-10">
            <span className="text-xs font-bold uppercase tracking-widest text-[#b39366]">Specialisering & Erfaring</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#041c2c] tracking-tight">
              Vores målgruppe
            </h2>
            <p className="text-slate-600 text-base sm:text-lg max-w-2xl mx-auto">
              Vi har erfaring med og kompetencer til at støtte borgere med bl.a.:
            </p>
          </div>

          {/* Elegant 2-Column List */}
          <div className="max-w-4xl mx-auto bg-[#faf9f6] rounded-2xl p-6 sm:p-10 border border-[#eae6dd] shadow-xs">
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
              {targetGroups.map((group, idx) => (
                <li
                  key={idx}
                  className="flex items-center gap-3.5 py-2 border-b border-slate-200/60 last:border-b-0 md:last:border-b"
                >
                  <span className="w-6 h-6 rounded-full bg-[#b39366]/15 text-[#b39366] flex items-center justify-center text-xs shrink-0 font-bold">
                    <i className="fa-solid fa-check text-[11px]" aria-hidden="true"></i>
                  </span>
                  <span className="text-slate-800 text-base font-medium">
                    {group}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* 4. VORES YDELSER - NICELY STYLED (Target Element updated) */}
      <section className="py-16 sm:py-20 lg:py-24 bg-[#f8f7f4] border-b border-[#eae6dd]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-4 mb-14">
            <span className="text-xs font-bold uppercase tracking-widest text-[#b39366]">Faglig indsats</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#041c2c] tracking-tight">
              Vores ydelser
            </h2>
            <div className="relative pt-2">
              <p className="text-slate-700 text-base sm:text-lg leading-relaxed font-normal bg-white/70 backdrop-blur-xs py-4 px-6 rounded-xl border border-[#eae6dd] shadow-xs">
                Vi tilbyder vikarer samt tre specialiserede ydelser inden for det social- og socialpsykiatriske område: <strong className="text-[#041c2c] font-semibold">bostøtte</strong>, <strong className="text-[#041c2c] font-semibold">aflastning</strong> og <strong className="text-[#041c2c] font-semibold">akut anbringelse</strong>. Læs mere om hver enkelt ydelse på deres egen side.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, idx) => (
              <Link
                key={idx}
                to={service.path}
                className="group bg-white rounded-xl overflow-hidden border border-[#eae6dd] shadow-sm hover:shadow-lg hover:border-[#b39366] transition-all duration-200 flex flex-col focus:outline-none focus:ring-2 focus:ring-[#b39366]"
              >
                <div className="aspect-16/10 overflow-hidden relative bg-slate-100">
                  <img
                    src={getOptimizedImageUrl(service.img, 600, 80)}
                    alt={service.alt || service.title}
                    style={{ objectPosition: service.position || 'center' }}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute top-3 left-3 bg-[#041c2c]/90 text-[#b39366] w-9 h-9 rounded-lg flex items-center justify-center shadow-md">
                    <i className={`fa-solid ${service.icon} text-sm`} aria-hidden="true"></i>
                  </div>
                </div>

                <div className="p-6 flex-1 flex flex-col justify-between space-y-4">
                  <div className="space-y-2.5">
                    <h3 className="text-xl font-bold text-[#041c2c] group-hover:text-[#b39366] transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{service.desc}</p>
                  </div>

                  <div className="pt-2 flex items-center gap-2 text-sm font-semibold text-[#b39366] group-hover:text-[#a18155]">
                    <span>{service.btnText}</span>
                    <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover:translate-x-1" aria-hidden="true"></i>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 5. HVORFOR AKUTVIKAREN? */}
      <section className="relative py-16 sm:py-20 lg:py-24 text-white border-b border-slate-800 overflow-hidden">
        {/* Background image with dark transparent overlay */}
        <div className="absolute inset-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=85,format=auto/assets/001da5eb-368a-49cd-a912-65694d60b551/akut-vikaren-omsorg-og-naerhed.webp"
            alt="Omsorg og nærhed"
            className="w-full h-full object-cover object-center"
            loading="lazy"
          />
        </div>
        <div className="absolute inset-0 bg-[#041c2c]/80" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-4 mb-12 sm:mb-16">
            <span className="text-xs font-bold uppercase tracking-widest text-[#b39366]">Vores styrker</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight">
              Hvorfor Akutvikaren?
            </h2>
            <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
              Hos Akutvikaren ved vi, at hver opgave er forskellig, og at der ofte ikke er tid at spilde. Genkendelighed er nøgleordet for vores samarbejde – vi bestræber os altid på at matche den bedst egnede medarbejder til opgaven, fordi vi tror på, at relationen betyder noget i arbejdet med mennesker.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {whyChooseUs.map((item, idx) => (
              <div
                key={idx}
                className="bg-[#041c2c]/95 backdrop-blur-sm border border-slate-700/80 rounded-xl p-6 sm:p-7 space-y-3.5 shadow-lg"
              >
                <div className="w-12 h-12 rounded-full bg-[#b39366]/20 border border-[#b39366]/30 flex items-center justify-center text-[#b39366] text-xl">
                  <i className={`fa-solid ${item.icon}`} aria-hidden="true"></i>
                </div>
                <h3 className="text-lg font-bold text-white tracking-tight">{item.title}</h3>
                <p className="text-slate-300 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}

            {/* Direct Contact Card */}
            <div className="bg-[#062438]/95 backdrop-blur-sm border border-[#b39366]/60 rounded-xl p-6 sm:p-7 flex flex-col justify-between space-y-4 shadow-lg">
              <div className="space-y-2">
                <span className="text-xs font-semibold text-[#b39366] uppercase tracking-wider">Klar til handling</span>
                <h3 className="text-lg font-bold text-white">Brug for vikarhjælp lige nu?</h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  Vi dækker både akutte og planlagte opgaver i hele Jylland og på Fyn samt modtager borgere fra hele landet ved akut anbringelse.
                </p>
              </div>
              <a
                href="tel:+4523964042"
                className="inline-flex items-center justify-center gap-2 bg-[#b39366] hover:bg-[#a18155] text-white font-semibold py-3 px-4 rounded text-sm transition-all whitespace-nowrap"
              >
                <i className="fa-solid fa-phone text-xs" aria-hidden="true"></i>
                <span className="whitespace-nowrap">Ring (+45) 23 96 40 42</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 6. BLIV VIKAR SEKTION */}
      <section className="py-16 sm:py-20 lg:py-24 bg-[#faf9f6] border-b border-[#eae6dd]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#041c2c] rounded-3xl overflow-hidden shadow-2xl border border-slate-800 text-white">
            <div className="grid grid-cols-1 lg:grid-cols-12 items-center">
              {/* Left Column: Text and Exact Customer Quote */}
              <div className="p-8 sm:p-12 lg:p-14 lg:col-span-7 space-y-6">
                <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-slate-800 border border-slate-700 text-xs font-semibold text-[#b39366] uppercase tracking-wider">
                  <i className="fa-solid fa-user-plus text-xs" aria-hidden="true"></i>
                  <span>Bliv vikar hos Akutvikaren</span>
                </div>

                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight leading-tight">
                  Vil du være en del af et fagligt stærkt vikarteam?
                </h2>

                <div className="p-4 sm:p-5 rounded-xl bg-slate-900/90 border-l-4 border-[#b39366] text-slate-200 shadow-inner">
                  <p className="text-xs font-bold uppercase tracking-wider text-[#b39366] mb-1">
                    Hvem ansætter vi?
                  </p>
                  <p className="text-sm sm:text-base font-semibold leading-relaxed text-white">
                    Vi ansætter social og sundheds faglige uddannet medarbejder eller personer med minimum tre års relevant erhvervs erfaring ..
                  </p>
                </div>

                <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                  Hos Akutvikaren får du stor indflydelse på dine egne vagter, faglig sparring, løbende undervisning og døgnåben bagvagtsstøtte (24/7). Vi søger både SSA, SSH, pædagoger, pædagogiske assistenter, sygeplejersker og erfarne profiler i hele Jylland og på Fyn.
                </p>

                <div className="flex flex-wrap items-center gap-4 pt-2">
                  <Link
                    to="/bliv-vikar"
                    className="inline-flex items-center gap-2 bg-[#b39366] hover:bg-[#a18155] text-white font-semibold text-sm px-6 py-3.5 rounded shadow-sm hover:shadow transition-all"
                  >
                    <span>Læs mere og søg her</span>
                    <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                  </Link>

                  <a
                    href="tel:+4523964042"
                    className="inline-flex items-center gap-2 text-slate-300 hover:text-white text-sm font-semibold px-4 py-3.5 rounded border border-slate-700 hover:border-slate-600 transition-colors"
                  >
                    <i className="fa-solid fa-phone text-[#b39366] text-xs" aria-hidden="true"></i>
                    <span>(+45) 23 96 40 42</span>
                  </a>
                </div>
              </div>

              {/* Right Column: Photo */}
              <div className="lg:col-span-5 h-72 sm:h-96 lg:h-full min-h-[360px] relative">
                <img
                  src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/001da5eb-368a-49cd-a912-65694d60b551/akut-vikaren-medarbejder-portraet.webp', 800, 85)}
                  alt="Medarbejder hos Akutvikaren"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#041c2c] via-transparent to-transparent lg:bg-gradient-to-l lg:from-transparent lg:to-[#041c2c]" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. KONTAKT SECTION */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-3 mb-12 sm:mb-16">
            <span className="text-xs font-bold uppercase tracking-widest text-[#b39366]">Kontakt</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#041c2c] tracking-tight">
              Har I brug for hjælp – nu eller på sigt?
            </h2>
            <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
              Uanset om I står i en akut situation, eller I er ved at planlægge en kommende indsats, er I velkomne til at kontakte os. Vi dækker hele Jylland og Fyn.
            </p>
            <p className="text-slate-500 text-sm">
              Ring til os, eller udfyld kontaktformularen – vi vender tilbage hurtigst muligt.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Left Info Column */}
            <div className="lg:col-span-5 space-y-6">
              <div className="bg-[#f8f7f4] border border-[#eae6dd] rounded-2xl p-6 sm:p-8 space-y-6">
                <h3 className="text-xl font-bold text-[#041c2c]">Akut Vikaren ApS</h3>

                <div className="space-y-4">
                  {/* Telefon */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#b39366]/20 text-[#b39366] flex items-center justify-center shrink-0 mt-0.5">
                      <i className="fa-solid fa-phone text-base" aria-hidden="true"></i>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Telefon (24/7)</p>
                      <a
                        href="tel:+4523964042"
                        className="text-base font-bold text-[#041c2c] hover:text-[#b39366] transition-colors whitespace-nowrap block"
                      >
                        (+45) 23 96 40 42
                      </a>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#b39366]/20 text-[#b39366] flex items-center justify-center shrink-0 mt-0.5">
                      <i className="fa-solid fa-envelope text-base" aria-hidden="true"></i>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">E-mail</p>
                      <a
                        href="mailto:kontakt@akutvikaren.dk"
                        className="text-base font-semibold text-[#041c2c] hover:text-[#b39366] transition-colors whitespace-nowrap block"
                      >
                        kontakt@akutvikaren.dk
                      </a>
                    </div>
                  </div>

                  {/* Adresse */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#b39366]/20 text-[#b39366] flex items-center justify-center shrink-0 mt-0.5">
                      <i className="fa-solid fa-location-dot text-base" aria-hidden="true"></i>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Adresse</p>
                      <p className="text-sm font-medium text-[#041c2c]">Alphavej 17, 8270 Højbjerg</p>
                      <a
                        href="https://maps.app.goo.gl/CGnJQRshRRNBUviN6"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs text-[#b39366] font-semibold hover:underline mt-1"
                      >
                        <span>Find vej på Google Maps</span>
                        <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true"></i>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Form Column */}
            <div className="lg:col-span-7">
              <div className="bg-[#f8f7f4] border border-[#eae6dd] rounded-2xl p-0">
                <ContactForm formTitle="Kontaktformular (Forside)" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
